// frontend/src/components/CampaignManager/tabs/ActionsTab.tsx
import React, { useState, useMemo } from 'react';
import {
  Box, Button, Stack, Typography, TableContainer, Table, TableHead, TableRow,
  TableCell, TableBody, Paper, IconButton, useMediaQuery, useTheme, Alert, Chip, Tooltip,
  FormControl, InputLabel, Select, MenuItem, Checkbox, TableSortLabel, TablePagination, TextField, InputAdornment
} from '@mui/material';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Plus, PencilSimple, Trash, CopySimple } from '@phosphor-icons/react';
import { ActionCard } from '../MobileCards';
import { Action, Campaign, Lottery } from '../types';

type SortField = 'wkz' | 'campaign' | 'werbeweg' | 'vermittler' | 'werbetraeger';
type SortDirection = 'asc' | 'desc';

// Strip lottery suffix from campaign name (e.g. "Inaktive - SKL 147" -> "Inaktive")
const stripLotterySuffix = (name: string): string => {
  if (!name) return name;
  return name.replace(/\s+-\s+(SKL|NKL|GKL)\s+\d+$/i, '').trim();
};

// Format WKZ with leading zeros to 5 digits
const formatWKZ = (wkz: string | number | undefined | null): string => {
  if (wkz === undefined || wkz === null || wkz === '') return '-';
  const str = String(wkz).replace(/^0+/, ''); // Remove existing leading zeros first
  return str.padStart(5, '0');
};

interface Props {
  actions: Action[];
  campaigns: Campaign[];
  lotteries: Lottery[];
  deleteAction: (id: number) => Promise<void>;
  onOpenDialog: (action: Action | null) => void;
  onOpenCopyDialog: (action: Action) => void;
  onOpenBulkCopyDialog: (actionIds: number[], sourceLotteryId?: number) => void;
}

export default function ActionsTab({ actions, campaigns, lotteries, deleteAction, onOpenDialog, onOpenCopyDialog, onOpenBulkCopyDialog }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedLotteryId, setSelectedLotteryId] = useState<number | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedActions, setSelectedActions] = useState<number[]>([]);
  const [sortField, setSortField] = useState<SortField>('wkz');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Filter actions by selected lottery and search term
  const filteredActions = useMemo(() => {
    let filtered = selectedLotteryId === 'all'
      ? actions
      : actions.filter(a => a.lotterie_id === selectedLotteryId);

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      // Remove leading zeros for numeric search
      const termWithoutLeadingZeros = term.replace(/^0+/, '');
      filtered = filtered.filter(a => {
        const keyAktion = a.key_aktion?.toLowerCase() || '';
        const wkz = a.wkz?.toLowerCase() || '';
        // Match with or without leading zeros
        return keyAktion.includes(term) ||
               keyAktion.includes(termWithoutLeadingZeros) ||
               wkz.includes(term) ||
               wkz.includes(termWithoutLeadingZeros);
      });
    }

    return filtered;
  }, [actions, selectedLotteryId, searchTerm]);

  // Sort actions
  const sortedActions = useMemo(() => {
    const sorted = [...filteredActions].sort((a, b) => {
      let aVal: string = '';
      let bVal: string = '';

      switch (sortField) {
        case 'wkz':
          aVal = a.key_aktion || '';
          bVal = b.key_aktion || '';
          break;
        case 'campaign':
          aVal = a.campaign_name || '';
          bVal = b.campaign_name || '';
          break;
        case 'werbeweg':
          aVal = a.werbeweg_name || '';
          bVal = b.werbeweg_name || '';
          break;
        case 'vermittler':
          aVal = a.vermittler_name || '';
          bVal = b.vermittler_name || '';
          break;
        case 'werbetraeger':
          aVal = a.werbetraeger_name || '';
          bVal = b.werbetraeger_name || '';
          break;
      }

      const comparison = aVal.localeCompare(bVal, 'de', { numeric: true });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredActions, sortField, sortDirection]);

  // Paginate actions
  const paginatedActions = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedActions.slice(start, start + rowsPerPage);
  }, [sortedActions, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Aktion wirklich löschen?')) {
      await deleteAction(id);
    }
  };

  const handleSelectAction = (id: number) => {
    setSelectedActions(prev =>
      prev.includes(id)
        ? prev.filter(aId => aId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const pageActionIds = paginatedActions.map(a => a.id);
    const allPageSelected = pageActionIds.every(id => selectedActions.includes(id));

    if (allPageSelected) {
      // Deselect all on current page
      setSelectedActions(prev => prev.filter(id => !pageActionIds.includes(id)));
    } else {
      // Select all on current page (add to existing selection)
      setSelectedActions(prev => {
        const combined = [...prev, ...pageActionIds];
        return combined.filter((id, index) => combined.indexOf(id) === index);
      });
    }
  };

  const handleBulkCopy = () => {
    const sourceLotteryId = selectedLotteryId !== 'all' ? selectedLotteryId : undefined;
    onOpenBulkCopyDialog(selectedActions, sourceLotteryId);
    setSelectedActions([]);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Aktionen</Typography>
        <Button
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          startIcon={<Plus size={20} />}
          onClick={() => onOpenDialog(null)}
          sx={{ backgroundColor: '#003366' }}
          disabled={campaigns.length === 0}
        >
          {isSmallMobile ? 'Neu' : 'Neue Aktion'}
        </Button>
      </Stack>

      {campaigns.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Bitte erstellen Sie zuerst eine Kampagne, bevor Sie eine Aktion anlegen.
        </Alert>
      )}

      {/* Search, Filter and Bulk Actions */}
      <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ mb: 2 }} alignItems={isMobile ? 'stretch' : 'center'}>
        <TextField
          size="small"
          placeholder="WKZ suchen..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: isMobile ? '100%' : 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlass size={18} />
              </InputAdornment>
            ),
          }}
        />
        {lotteries.length > 0 && (
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 250 }}>
            <InputLabel>Lotterie filtern</InputLabel>
            <Select
              value={selectedLotteryId}
              onChange={(e) => {
                setSelectedLotteryId(e.target.value as number | 'all');
                setSelectedActions([]);
                setPage(0);
              }}
              label="Lotterie filtern"
            >
              <MenuItem value="all">
                <em>Alle Lotterien ({actions.length})</em>
              </MenuItem>
              {lotteries.map((lottery) => {
                const count = actions.filter(a => a.lotterie_id === lottery.id).length;
                return (
                  <MenuItem key={lottery.id} value={lottery.id}>
                    {lottery.name} ({lottery.type} {lottery.year}) - {count} Aktion{count !== 1 ? 'en' : ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}
        {selectedActions.length > 0 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<CopySimple size={18} />}
            onClick={handleBulkCopy}
            sx={{ backgroundColor: '#0066cc' }}
          >
            {selectedActions.length} kopieren
          </Button>
        )}
        {(searchTerm || selectedLotteryId !== 'all') && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredActions.length} von {actions.length} Aktionen
          </Typography>
        )}
      </Stack>

      {isMobile ? (
        sortedActions.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {actions.length === 0 ? 'Keine Aktionen vorhanden' : 'Keine Aktionen für diese Lotterie'}
            </Typography>
          </Paper>
        ) : (
          <>
            {paginatedActions.map((action) => (
              <ActionCard key={action.id} action={action} onEdit={onOpenDialog} onDelete={handleDelete} />
            ))}
            {sortedActions.length > rowsPerPage && (
              <TablePagination
                component="div"
                count={sortedActions.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[50, 100, 250, 500]}
                labelRowsPerPage="Pro Seite:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
              />
            )}
          </>
        )
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={paginatedActions.length > 0 && paginatedActions.every(a => selectedActions.includes(a.id))}
                    indeterminate={paginatedActions.some(a => selectedActions.includes(a.id)) && !paginatedActions.every(a => selectedActions.includes(a.id))}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sortDirection={sortField === 'wkz' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'wkz'}
                    direction={sortField === 'wkz' ? sortDirection : 'asc'}
                    onClick={() => handleSort('wkz')}
                  >
                    WKZ
                  </TableSortLabel>
                </TableCell>
                <TableCell>WKZ-Text</TableCell>
                <TableCell sortDirection={sortField === 'campaign' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'campaign'}
                    direction={sortField === 'campaign' ? sortDirection : 'asc'}
                    onClick={() => handleSort('campaign')}
                  >
                    Kampagne
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'werbeweg' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'werbeweg'}
                    direction={sortField === 'werbeweg' ? sortDirection : 'asc'}
                    onClick={() => handleSort('werbeweg')}
                  >
                    Werbeweg
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'werbetraeger' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'werbetraeger'}
                    direction={sortField === 'werbetraeger' ? sortDirection : 'asc'}
                    onClick={() => handleSort('werbetraeger')}
                  >
                    Werbeträger
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'vermittler' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'vermittler'}
                    direction={sortField === 'vermittler' ? sortDirection : 'asc'}
                    onClick={() => handleSort('vermittler')}
                  >
                    Vermittler
                  </TableSortLabel>
                </TableCell>
                <TableCell>Callbacks</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedActions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {actions.length === 0 ? 'Keine Aktionen vorhanden' : 'Keine Aktionen für diese Lotterie'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedActions.map((action) => (
                  <TableRow
                    key={action.id}
                    hover
                    selected={selectedActions.includes(action.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedActions.includes(action.id)}
                        onChange={() => handleSelectAction(action.id)}
                      />
                    </TableCell>
                    <TableCell><strong style={{ fontFamily: 'monospace' }}>{formatWKZ(action.key_aktion)}</strong></TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={action.wkz || ''}>
                        <span>{action.wkz || '-'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{stripLotterySuffix(action.campaign_name || '') || `Kampagne ${action.campaign_id}`}</TableCell>
                    <TableCell>{action.werbeweg_name || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={action.werbetraeger_name || ''}>
                        <span>{action.werbetraeger_name || '-'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{action.vermittler_name || '-'}</TableCell>
                    <TableCell>
                      {action.callbacks_count ? (
                        <Chip
                          label={action.callbacks_count.toLocaleString('de-DE')}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {action.aktiv !== false ? (
                        <Chip label="Aktiv" size="small" color="success" />
                      ) : (
                        <Chip label="Inaktiv" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Bearbeiten">
                        <IconButton size="small" onClick={() => onOpenDialog(action)}>
                          <PencilSimple size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="In andere Lotterie kopieren">
                        <IconButton size="small" onClick={() => onOpenCopyDialog(action)} color="primary">
                          <CopySimple size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton size="small" color="error" onClick={() => handleDelete(action.id)}>
                          <Trash size={18} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={sortedActions.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[50, 100, 250, 500]}
            labelRowsPerPage="Pro Seite:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
          />
        </TableContainer>
      )}
    </Box>
  );
}
